import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useContext, useRef, useLayoutEffect, useState } from 'react';
import Box from '../Box';
import CheckboxGroupContext from '../CheckboxGroup/CheckboxGroupContext';
import Icon from '../Icon';
import Link from '../Link';
import StackView from '../StackView';
import Text from '../Text';
import VariantProvider from '../VariantProvider';
import Tooltip from '../Tooltip';
import { useThemeValue } from '../system';
import { useAccessibilityViolation } from '../hooks/use-accessibility-violation';
import { mergeProps } from '../utils';
var iconPaths = {
  fill: 'checkbox-0',
  minus: 'checkbox-1',
  checked: 'checkbox-2'
};

var _ref2 = /*#__PURE__*/React.createElement(Link, {
  external: true,
  to: "https://ministrycentered.github.io/ui-kit/checkbox#label",
  weight: 700
}, "label");

function Checkbox(_ref) {
  var checked = _ref.checked,
      disabled = _ref.disabled,
      id = _ref.id,
      indeterminate = _ref.indeterminate,
      label = _ref.label,
      name = _ref.name,
      _onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      _onFocus = _ref.onFocus,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'sm' : _ref$size,
      value = _ref.value,
      restProps = _objectWithoutPropertiesLoose(_ref, ["checked", "disabled", "id", "indeterminate", "label", "name", "onBlur", "onChange", "onFocus", "size", "value"]);

  var checkboxTheme = useThemeValue('checkbox');
  var checkboxGroup = useContext(CheckboxGroupContext);

  var _useState = useState(false),
      localChecked = _useState[0],
      setLocalChecked = _useState[1];

  var _useState2 = useState(false),
      focus = _useState2[0],
      setFocus = _useState2[1];

  var isChecked = checkboxGroup ? checkboxGroup.value.indexOf(value) > -1 : checked === undefined ? localChecked : checked;
  var tooltip = null;

  if (process.env.NODE_ENV !== 'production') {
    var ref = useRef(null);

    var _useState3 = useState(false),
        needsParentLabel = _useState3[0],
        setNeedsParentLabel = _useState3[1];

    var _useAccessibilityViol = useAccessibilityViolation( /*#__PURE__*/React.createElement(React.Fragment, null, "Checkbox must define a", ' ', _ref2, ' ', "through props or a parent element that is a label."), "UIKit: <Checkbox/> must define a \"label\" prop or have a parent that renders a label for proper accessibility."),
        tooltipProps = _useAccessibilityViol.tooltipProps,
        wrapperProps = _useAccessibilityViol.wrapperProps;

    useLayoutEffect(function () {
      function findParent(node, findMatch) {
        if (node && node !== document.documentElement) {
          var matchFound = findMatch(node.parentNode);

          if (matchFound) {
            return node.parentNode;
          } else {
            return findParent(node.parentNode, findMatch);
          }
        } else {
          return null;
        }
      }

      if (label === undefined) {
        var labelParent = findParent(ref.current, function (parentNode) {
          return parentNode.tagName === 'LABEL';
        });

        if (!labelParent) {
          setNeedsParentLabel(true);
        }
      }
    }, []);
    restProps = mergeProps(restProps, {
      innerRef: ref
    });

    if (needsParentLabel) {
      tooltip = tooltipProps;
      restProps = mergeProps(restProps, wrapperProps);
    }
  }

  return /*#__PURE__*/React.createElement(VariantProvider, {
    checked: isChecked,
    indeterminate: indeterminate,
    focus: focus,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(Tooltip, tooltip, /*#__PURE__*/React.createElement(StackView, _extends({
    as: label ? 'label' : 'span',
    inline: true,
    axis: "horizontal",
    alignment: "center",
    shrink: 0,
    position: "relative",
    spacing: 0.5,
    variants: {
      disabled: {
        opacity: 0.5,
        cursor: 'not-allowed'
      }
    }
  }, restProps), /*#__PURE__*/React.createElement(Box, {
    as: "input",
    type: "checkbox",
    id: id,
    name: checkboxGroup ? checkboxGroup.name : name,
    checked: isChecked,
    value: value,
    disabled: disabled,
    onChange: checkboxGroup ? checkboxGroup.onChange : function (event) {
      setLocalChecked(event.target.checked);
      onChange && onChange(event);
    },
    onFocus: function onFocus(event) {
      setFocus(true);
      _onFocus && _onFocus(event);
    },
    onBlur: function onBlur(event) {
      setFocus(false);
      _onBlur && _onBlur(event);
    },
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 0,
    opacity: 0
  }), /*#__PURE__*/React.createElement(Icon, {
    size: size,
    zIndex: 1
  }, /*#__PURE__*/React.createElement(Icon.Path, {
    name: iconPaths.fill,
    fill: checkboxTheme.fill,
    stroke: checkboxTheme.stroke,
    transform: "scale(1)",
    transformOrigin: "center center",
    transition: "all 180ms ease-out",
    variants: {
      focus: {
        stroke: checkboxTheme.focusStroke
      },
      active: {
        transform: 'scale(1)'
      },
      checked: {
        fill: checkboxTheme.checkedFill,
        stroke: checkboxTheme.checkedStroke
      },
      indeterminate: {
        fill: 'primary-light',
        stroke: 'primary'
      }
    }
  }), /*#__PURE__*/React.createElement(Icon.Path, {
    name: iconPaths.minus,
    fill: "#fff",
    transform: "scale(0)",
    transformOrigin: "center center",
    transition: "all 180ms ease-out",
    variants: {
      indeterminate: {
        visibility: 'visible',
        transform: 'scale(1)',
        transition: 'all 120ms ease-out'
      }
    }
  }), /*#__PURE__*/React.createElement(Icon.Path, {
    name: iconPaths.checked,
    fill: "#fff",
    transform: "scale(0)",
    transformOrigin: "center center",
    transition: "all 180ms ease-out",
    variants: {
      checked: {
        visibility: 'visible',
        transform: 'scale(1)',
        transition: 'all 120ms ease-out'
      }
    }
  })), label && /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    lineHeight: 2.5
  }, label))));
}

export default Checkbox;